import React, { useState, useEffect, useCallback } from 'react';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { useAuth } from '../../context/AuthContext';
import { useEnvironment } from '../../context/EnvironmentContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { MessageCircle, RefreshCw, User, Building2, Calendar, FileText, MessageSquare, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { toast } from '../../lib/safeToast';

const STATUS_BADGE = {
  open: 'bg-amber-50 text-amber-700 border-amber-200',
  responded: 'bg-blue-50 text-blue-700 border-blue-200',
  closed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const STATUS_ICON = {
  open: AlertCircle,
  responded: CheckCircle,
  closed: CheckCircle,
};

const FRIENDLY_LABELS = {
  co_recommended_approve: 'Compliance Approved',
  co_recommended_reject: 'Compliance Rejected',
  pending_su_approval: 'Awaiting SU Decision',
  pending_su_review: 'Awaiting SU Decision',
};
const label = (s) =>
  FRIENDLY_LABELS[s] || (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

function truncate(str, len = 80) {
  if (!str) return '—';
  return str.length > len ? str.slice(0, len) + '…' : str;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return '—'; }
}

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString('en-AU', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
}

function DetailRow({ icon: Icon, label: lbl, children }) {
  return (
    <div className="flex items-start gap-3 py-2.5">
      <div className="w-8 h-8 rounded-lg bg-[#E6F7F3] flex items-center justify-center shrink-0 mt-0.5">
        <Icon className="w-4 h-4 text-[#0B6445]" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-0.5">{lbl}</p>
        <div className="text-sm text-[#0F172A]">{children}</div>
      </div>
    </div>
  );
}

export default function BackOfficeComplianceInfoRequests() {
  const { api } = useAuth();
  const { isLive } = useEnvironment();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/backoffice/kyc/info-requests');
      setRows(res.data?.data || []);
    } catch {
      setRows([]);
      toast.error('Failed to load information requests');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData, isLive]);

  return (
    <BackOfficeLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between pb-4">
          <div>
            <h1 className="text-2xl font-bold text-[#073B2E] flex items-center gap-2">
              <MessageCircle className="w-7 h-7 text-[#0B6445]" />
              Information Requests
            </h1>
            <p className="text-[#0E1B16]/70 mt-2">Requests for additional information sent to merchants.</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            className="border-[#1C472B] text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E]"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
        </div>

        <Card className="border-[#DCE4E0] shadow-sm">
          <CardContent className="pt-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="spinner w-8 h-8" />
              </div>
            ) : rows.length === 0 ? (
              <p className="text-center py-12 text-[#0E1B16]/60">No information requests found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#DCE4E0]">
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Merchant</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Requested By</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Message</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Response</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Status</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr
                        key={row.id || i}
                        className="border-b border-[#DCE4E0]/80 hover:bg-[#F0FAF6] cursor-pointer transition-colors"
                        onClick={() => setSelected(row)}
                      >
                        <td className="py-3 px-2">
                          <div>
                            <p className="font-medium text-[#073B2E]">{row.merchant_name || '—'}</p>
                            {row.company_name && (
                              <p className="text-xs text-slate-500">{row.company_name}</p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-2">{row.requested_by_name || '—'}</td>
                        <td className="py-3 px-2 max-w-[220px]" title={row.message || ''}>
                          {truncate(row.message, 60)}
                        </td>
                        <td className="py-3 px-2 max-w-[220px]" title={row.merchant_response || ''}>
                          {row.merchant_response
                            ? <span className="text-emerald-700">{truncate(row.merchant_response, 60)}</span>
                            : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="py-3 px-2">
                          <Badge
                            variant="outline"
                            className={STATUS_BADGE[row.status] || 'bg-slate-50 text-slate-600 border-slate-200'}
                          >
                            {label(row.status)}
                          </Badge>
                        </td>
                        <td className="py-3 px-2 whitespace-nowrap">
                          {formatDate(row.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(open) => { if (!open) setSelected(null); }}>
        <DialogContent className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-[#DCE4E0]">
            <DialogTitle className="text-lg font-bold text-[#073B2E]">
              Information Request Details
            </DialogTitle>
            {selected && (
              <div className="flex items-center gap-3 mt-3">
                <Badge
                  variant="outline"
                  className={`${STATUS_BADGE[selected.status] || 'bg-slate-50 text-slate-600 border-slate-200'} text-sm px-3 py-1`}
                >
                  {label(selected.status)}
                </Badge>
                <span className="text-xs text-slate-500">{formatDateTime(selected.created_at)}</span>
              </div>
            )}
          </DialogHeader>

          {selected && (
            <div className="px-6 py-5 space-y-1">
              {/* Merchant info */}
              <DetailRow icon={User} label="Merchant">
                <p className="font-medium">{selected.merchant_name || '—'}</p>
                {selected.merchant_email && (
                  <p className="text-xs text-slate-500 mt-0.5">{selected.merchant_email}</p>
                )}
              </DetailRow>

              {selected.company_name && (
                <DetailRow icon={Building2} label="Company">
                  {selected.company_name}
                </DetailRow>
              )}

              <DetailRow icon={User} label="Requested By">
                {selected.requested_by_name || selected.requested_by || '—'}
              </DetailRow>

              <DetailRow icon={Calendar} label="Date Requested">
                {formatDateTime(selected.created_at)}
              </DetailRow>

              {/* Divider */}
              <div className="border-t border-[#DCE4E0] my-4" />

              {/* Message */}
              <DetailRow icon={MessageSquare} label="Request Message">
                <p className="whitespace-pre-wrap text-slate-700 leading-relaxed">{selected.message || '—'}</p>
              </DetailRow>

              {/* Missing items */}
              {selected.missing_items && selected.missing_items.length > 0 && (
                <DetailRow icon={FileText} label="Required Items">
                  <ul className="space-y-1.5 mt-1">
                    {selected.missing_items.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0 text-xs font-bold mt-0.5">
                          {idx + 1}
                        </span>
                        <span className="text-sm text-slate-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </DetailRow>
              )}

              {/* Merchant response */}
              {selected.merchant_response && (
                <>
                  <div className="border-t border-[#DCE4E0] my-4" />
                  <div className="rounded-lg bg-blue-50/60 border border-blue-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      {(() => { const I = STATUS_ICON.responded; return <I className="w-4 h-4 text-blue-600" />; })()}
                      <p className="text-sm font-semibold text-blue-800">Merchant Response</p>
                      {selected.responded_at && (
                        <span className="text-xs text-blue-500 ml-auto">{formatDateTime(selected.responded_at)}</span>
                      )}
                    </div>
                    <p className="text-sm text-blue-900 whitespace-pre-wrap leading-relaxed">
                      {selected.merchant_response}
                    </p>
                  </div>
                </>
              )}

              {/* No response yet */}
              {!selected.merchant_response && selected.status === 'open' && (
                <>
                  <div className="border-t border-[#DCE4E0] my-4" />
                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 flex items-center gap-3">
                    <Clock className="w-5 h-5 text-slate-400" />
                    <p className="text-sm text-slate-500">Awaiting merchant response…</p>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </BackOfficeLayout>
  );
}
